import { Button, Form } from "react-bootstrap";
import ProviderTable from "../../components/dashboard/provider/ProviderTable";
import { PageConfig } from "../../router";
import {
  ChangeEvent,
  MutableRefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getProviders } from "../../utils/rest-api/providers";
import { useApp } from "../../contexts/AppContext";
import MetaComponent from "../../components/common/MetaComponent";

const metadata = {
  title: "Luna - Booking Page",
  description: "Luna - Booking Page",
};

interface ProviderFilterProp {
  query: string;
  status: string;
  page: number;
}

export default function ProviderPage() {
  const [providers, setProviders] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filter, setFilter] = useState<ProviderFilterProp>(() => ({
    query: searchParams.get("q") ?? "",
    status: searchParams.get("status") ?? "all",
    page: parseInt(searchParams.get("page") ?? "1", 10) || 1,
  }));

  // Keep the URL in sync with the current filter so it survives back/forward navigation
  useEffect(() => {
    const params: Record<string, string> = { page: String(filter.page) };
    if (filter.query) params.q = filter.query;
    if (filter.status && filter.status !== "all") params.status = filter.status;
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    showLoader();
    getProviders(
      filter.query || undefined,
      filter.status === "all" ? undefined : filter.status === "active",
      filter.page
    )
      .then((res) => {
        const data = res.data.data;
        setProviders(data);
        const total = res.data.totalPages;
        setTotalPages(total);
      })
      .finally(() => hideLoader());
  }, [filter]);

  let timeoutId: MutableRefObject<NodeJS.Timeout | undefined> =
    useRef(undefined);

  const [query, setQuery] = useState(filter.query);
  const onQueryChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.currentTarget.value);
    if (timeoutId && timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = undefined;
    }
    timeoutId.current = setTimeout(
      (queryValue) => {
        setFilter((prev) => ({ ...prev, page: 1, query: queryValue }));
      },
      500,
      e.currentTarget.value
    );
  };

  const statuses = ["all", "active", "inactive"];
  const onStatusChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setFilter((prev) => ({ ...prev, page: 1, status: e.currentTarget.value }));
  };

  return (
    <>
      <MetaComponent meta={metadata} />
      <div className="dashboard__main h-100">
        <div className="dashboard__content bg-light-2 h-100 w-100">
          <div className="d-flex flex-column h-100 md-overflow-y-scroll">
            <div className="d-flex flex-row mb-20">
              <span className="text-30 lh-14 fw-600">Provider</span>
            </div>
            <div
              className="d-flex md:flex-column flex-row mb-20"
              style={{ gap: "5px" }}
            >
              <Form.Group
                className="w-filter-control"
                style={{ height: "100%" }}
              >
                <Form.Control
                  style={{ height: "100%" }}
                  type="text"
                  name="query"
                  value={query}
                  onChange={onQueryChange}
                  placeholder="Search"
                />
              </Form.Group>
              <Form.Select
                className="w-filter-control"
                style={{ height: "100%" }}
                value={filter.status}
                onChange={onStatusChange}
              >
                {statuses.map((status) => {
                  return (
                    <option key={`status-${status}`} value={status}>{`${status
                      .charAt(0)
                      .toUpperCase()}${status.slice(1)}`}</option>
                  );
                })}
              </Form.Select>
              <div className="flex-grow-1 md:d-none"></div>
              <Button
                variant="primary"
                onClick={(e) => navigate("/providers/add")}
              >
                Add Provider
              </Button>
            </div>
            <ProviderTable
              data={providers}
              className="h-100 md-overflow-y-visible overflow-y-scroll"
              total={totalPages}
              page={filter.page}
              onPageChange={(page) => {
                setFilter((prev) => ({ ...prev, page }));
              }}
              onEdit={(provider) => navigate(`/providers/${provider._id}`)}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export const ProviderPageConfig: PageConfig = {
  path: "/providers",
  component: ProviderPage,
  roles: ["admin", "root"],
};
